import { useContext, useEffect, useState } from "react"
import { AppContext } from "../App"
import { callApi, formatDate, formatMoney, getInlineLoader, parseJson } from "../Helpers";
import {
  STR_AREA,
  STR_COULD_NOT_FETCH_DATA,
  STR_CORNER_POINTS,
  STR_INQUIRY_DETAILS,
  STR_LAND_POLYGON,
  STR_LINE_LENGTH,
  STR_LOCATION,
  STR_MANUAL_IMPORTATION,
  STR_MAPPING_TYPE,
  STR_METERS,
  STR_MOBILE_GPS_MAPPING,
  STR_NO_RECORD,
  STR_POLYLINE,
  STR_SELECTED_GEOMETRY,
  STR_SERVICES,
  STR_SQUARE_METERS,
  STR_PERIMETER,
} from "../Strings";
import InquiryGeometryMap from "../components/InquiryGeometryMap";
import CoordinateCards from "../components/CoordinateCards";
import MappingSourceDetails from "../components/MappingSourceDetails";
import RequestedServiceCard from "../components/RequestedServiceCard";
import {
  MANUAL_IMPORTATION,
  MOBILE_GPS_MAPPING,
} from "../CoordinateSystems";

export default function ViewOrder({ uuid }) {

  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);
  const [data, setData] = useState(null);
  const [availableFollowUps, setAvailableFollowUps] = useState([]);
  const [requestingService, setRequestingService] = useState(null);

  async function init() {
    setReady(false);
    await getData();
    await getAvailableFollowUps();
    setReady(true);
  }

  function getData() {
    return new Promise(async resolve => {
      await callApi("get-order-data", {
        orderUuid: uuid,
      }).then((response) => {
        if (response.status === 1) {
          setData(response.data);
        } else {
          appContext.tellError(STR_COULD_NOT_FETCH_DATA[appContext.language]);
        }

        resolve(response);
      })
    })
  }

  function getAvailableFollowUps() {
    return new Promise(async resolve => {
      await callApi("get-available-follow-up-services", {
        orderUuid: uuid,
      }).then((response) => {
        setAvailableFollowUps(response.status === 1 ? response.data : []);
        resolve(response);
      })
    })
  }

  async function requestFollowUp(service) {
    setRequestingService(service.uuid);
    const response = await callApi("request-follow-up-service", {
      orderUuid: uuid,
      serviceUuid: service.uuid,
      parentRequestedServiceUuid: service.parentRequestedServiceUuid,
    });
    if (response.status === 1) {
      appContext.tellMessage("Follow-up service requested");
      await init();
    } else appContext.tellError(response.msg);
    setRequestingService(null);
  }

  function confirmFollowUpRequest(service) {
    appContext.activateDialog({
      message: `Request ${service.titleEn} for this inquiry?`,
      onConfirm: () => requestFollowUp(service),
    })
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    appContext.setShowOverlayLoader(requestingService !== null);

    return () => {
      appContext.setShowOverlayLoader(false);
    }
  }, [requestingService])

  if (!ready) {
    return (
      <div style={{ padding: "40px" }}>
        {getInlineLoader()}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container" style={{ padding: "40px" }}>
        <h6>{STR_NO_RECORD[appContext.language]}</h6>
      </div>
    );
  }

  const location = parseJson(data.userLocation, {});
  const requestedServices = data.requestedServices || [];
  const cornerPoints = parseJson(data.cornerPoints, []);
  const pointsCrsMetadata = parseJson(data.pointsCrsMetadata, {});
  const originalCornerPoints = parseJson(data.originalCornerPoints, []);
  const pointsMappingMethod = data.pointsMappingMethod || MOBILE_GPS_MAPPING;
  const isManualImport = pointsMappingMethod === MANUAL_IMPORTATION;
  const isPolyline = cornerPoints.length === 2;
  const locationText = [
    location.region,
    location.district,
    location.ward,
    location.street,
  ].filter(Boolean).join(", ");

  return (
    <div className="container" style={{ paddingTop: "20px", paddingBottom: "100px" }}>
      <div className="row">
        <div className="col-12">
          <h6 className="main-section-title">
            {STR_INQUIRY_DETAILS[appContext.language]}
          </h6>
        </div>

        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h5 className="font-bold">{data.title}</h5>
              <p className="text-muted mb-0">{formatDate(data.dateCreated)}</p>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <h6 className="text-muted">{STR_MAPPING_TYPE[appContext.language]}</h6>
              <h5 className="font-bold">
                {isPolyline
                  ? STR_POLYLINE[appContext.language]
                  : STR_LAND_POLYGON[appContext.language]}
              </h5>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <h6 className="text-muted">{STR_AREA[appContext.language]}</h6>
              <h5 className="font-bold">
                {formatMoney(data.selectedAreaSqm)} {STR_SQUARE_METERS[appContext.language]}
              </h5>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <h6 className="text-muted">
                {isPolyline
                  ? STR_LINE_LENGTH[appContext.language]
                  : STR_PERIMETER[appContext.language]}
              </h6>
              <h5 className="font-bold">
                {formatMoney(data.selectedPerimeterMeters)} {STR_METERS[appContext.language]}
              </h5>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h6 className="text-muted">{STR_LOCATION[appContext.language]}</h6>
              <h5 className="font-semi-bold">{locationText || "N/A"}</h5>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h6 className="text-muted mb-3">{STR_SERVICES[appContext.language]}</h6>
              {requestedServices.map((service) => <RequestedServiceCard item={service} key={service.uuid} />)}
              {requestedServices.length === 0 ? <p className="text-muted">No services requested.</p> : null}
            </div>
          </div>
        </div>

        {availableFollowUps.length > 0 ? (
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h6 className="text-muted mb-3">Available Follow-up Services</h6>
                {availableFollowUps.map((service) => (
                  <div className="border-bottom py-3" key={`${service.parentRequestedServiceUuid}-${service.uuid}`}>
                    <h6 className="font-semi-bold">{appContext.language === "sw" ? service.titleSw : service.titleEn}</h6>
                    <p className="text-muted mb-2">Available after: {appContext.language === "sw" ? service.parentTitleSw : service.parentTitleEn}</p>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="font-bold">TZS {formatMoney(service.quote?.quotedAmount)}</span>
                      <button
                        className="btn btn-sm btn-primary"
                        disabled={requestingService === service.uuid}
                        onClick={() => confirmFollowUpRequest(service)}
                      >
                        Request service
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h6 className="text-muted mb-3">{STR_CORNER_POINTS[appContext.language]}</h6>
              <MappingSourceDetails
                mappingMethod={pointsMappingMethod}
                metadata={pointsCrsMetadata}
                originalPoints={originalCornerPoints}
              />
              <CoordinateCards
                points={cornerPoints}
                sourceLabel={
                  isManualImport
                    ? `${STR_MANUAL_IMPORTATION[appContext.language]} · WGS 84 normalized`
                    : STR_MOBILE_GPS_MAPPING[appContext.language]
                }
              />

              {
                cornerPoints.length > 0
                  ? <div className="mt-4">
                    <h6 className="text-muted mb-3">
                      {STR_SELECTED_GEOMETRY[appContext.language]}
                    </h6>
                    <InquiryGeometryMap points={cornerPoints} />
                  </div>
                  : ""
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
